/**
 * A client's documents by year and folder, shared by the staff client page and the client portal.
 * Every year has the same folders (grouped by service), so a year is ready before anything is
 * uploaded; the Permanent folders hold papers that don't belong to a year. Next year's folder
 * opens on December 1, and every earlier year stays open.
 */
import { CLIENT_UPLOAD_FOLDERS, FOLDER_GROUPS, FOLDER_HELP, PERMANENT_FOLDERS, folderGroup, isPermanentFolder, latestOpenYear } from "@clientflow/shared";
import { Archive, Download, FileText, FolderClosed, FolderOpen } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { ListSearch, matches, Pager, usePaged } from "@/components/page";
import { UploadButton } from "@/components/panels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatBytes, formatDate, localToday } from "@/lib/format";
import type { Doc } from "@/lib/types";

type Props = {
  docs: Doc[];
  /** Who is looking: clients choose from their folders only. */
  mode: "staff" | "portal";
  /** Where "Download all" goes: the zip URL for a year or for "permanent". */
  zipUrl: (year: number | "permanent") => string | null;
  /** Upload a file to a year (or the Permanent folders) and folder; omitted when uploads aren't allowed. */
  onUpload?: (file: File, year: number | null, folder: string) => void;
  /** Photos picked together as the pages of one document, sent as one PDF named `name`. */
  onCombine?: (files: File[], name: string, year: number | null, folder: string) => void;
  uploading?: boolean;
  /** Extra controls beside the upload button (staff: "Internal"). */
  uploadExtras?: ReactNode;
  /** Whether a file can go in "Download all" (portal: not while held for payment). */
  zippable?: (d: Doc) => boolean;
  /** A line under each file's name (size, date, entity). */
  meta: (d: Doc) => ReactNode;
  /** The controls at the end of each file's row. */
  actions: (d: Doc) => ReactNode;
};

const select = "h-9 min-w-0 rounded-md border border-input bg-background px-2 text-sm";

const isPhoto = (f: File) => /\.(jpe?g|png)$/i.test(f.name);

export function YearFolders({ docs, mode, zipUrl, onUpload, onCombine, uploading, uploadExtras, zippable = () => true, meta, actions }: Props) {
  const today = localToday();
  const thisYear = Number(today.slice(0, 4));
  const latest = latestOpenYear(today);
  const permanent = docs.filter((d) => isPermanentFolder(d.folder));
  const dated = docs.filter((d) => !isPermanentFolder(d.folder));
  const [extra, setExtra] = useState<number[]>([]);
  // Every year with files (work for a later year shows even before its folder opens for uploads),
  // this year, next year from December 1, and any year opened by hand.
  const years = useMemo(() => [...new Set([...dated.map((d) => d.year), thisYear, latest, ...extra])].sort((a, b) => b - a), [dated, thisYear, latest, extra]);
  // Open on this year, or on the latest year with files when this year is still empty.
  const withFiles = dated.map((d) => d.year).filter((y) => y <= thisYear);
  const start = withFiles.length === 0 || withFiles.includes(thisYear) ? thisYear : Math.max(...withFiles);
  const [picked, setPicked] = useState<number | "permanent">(start);
  const view = picked === "permanent" || years.includes(picked) ? picked : thisYear;
  // A later year's folder holds early work but takes uploads only from December 1 before it.
  const uploadOpen = view === "permanent" || view <= latest;
  const inView = view === "permanent" ? permanent : dated.filter((d) => d.year === view);
  const [search, setSearch] = useState("");
  const found = inView.filter((d) => matches(search, d.filename, d.folder));

  const choices = view === "permanent" ? PERMANENT_FOLDERS : mode === "portal" ? CLIENT_UPLOAD_FOLDERS.filter((f) => !isPermanentFolder(f)) : FOLDER_GROUPS.filter((g) => g.key !== "permanent").flatMap((g) => g.folders);
  const [folder, setFolder] = useState<string>("");
  const target = choices.includes(folder) ? folder : choices[0]!;

  // Any other year back to 2000 (a late return, an old document), not only years below the oldest tab.
  const older: number[] = [];
  for (let y = latest; y >= 2000; y--) if (!years.includes(y)) older.push(y);

  const groups = [
    ...FOLDER_GROUPS.filter((g) => (view === "permanent" ? g.key === "permanent" : g.key !== "permanent")),
    ...(view === "permanent" ? [] : [{ key: "other" as const, label: "Other", folders: [...new Set(inView.filter((d) => folderGroup(d.folder) === "other").map((d) => d.folder))] }]),
  ];
  const visibleFolders = new Set(mode === "portal" ? [...CLIENT_UPLOAD_FOLDERS, ...inView.map((d) => d.folder)] : groups.flatMap((g) => g.folders));
  const empty = groups.flatMap((g) => g.folders).filter((f) => visibleFolders.has(f) && !inView.some((d) => d.folder === f));
  const bytes = inView.reduce((a, d) => a + d.sizeBytes, 0);
  const zip = inView.some(zippable) ? zipUrl(view) : null;
  const taxHint = mode === "portal" && view !== "permanent" && folderGroup(target) === "tax";

  // Two or more photos picked together: ask whether they are pages of one document.
  const [photos, setPhotos] = useState<File[] | null>(null);
  const [asOne, setAsOne] = useState(false);
  const [docName, setDocName] = useState("");
  const uploadYear = view === "permanent" ? null : view;
  const pick = (files: File[]) => {
    if (onCombine && files.length >= 2 && files.length <= 30 && files.every(isPhoto)) {
      setPhotos(files);
      setAsOne(false);
      setDocName("");
      return;
    }
    for (const f of files) onUpload?.(f, uploadYear, target);
  };
  const sendPhotos = () => {
    if (!photos) return;
    if (asOne) onCombine?.(photos, docName.trim(), uploadYear, target);
    else for (const f of photos) onUpload?.(f, uploadYear, target);
    setPhotos(null);
  };

  return (
    <div className="space-y-4">
      {/* Year tabs: scroll sideways inside their own strip on a phone. */}
      <div className="-mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-1">
        <div
          role="tablist"
          aria-label="Year"
          className="flex shrink-0 items-center gap-2"
          onKeyDown={(e) => {
            // Arrow keys move between the tabs (one tab stop for the group).
            const keys = ["ArrowLeft", "ArrowRight", "Home", "End"];
            if (!keys.includes(e.key)) return;
            const tabs = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]'));
            const at = tabs.indexOf(document.activeElement as HTMLElement);
            if (at < 0) return;
            e.preventDefault();
            const next = e.key === "Home" ? 0 : e.key === "End" ? tabs.length - 1 : (at + (e.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
            tabs[next].focus();
            tabs[next].click();
          }}
        >
        <YearTab active={view === "permanent"} onClick={() => setPicked("permanent")} icon={<Archive className="h-3.5 w-3.5" />} label="Permanent" count={permanent.length} />
        {years.map((y) => (
          <YearTab key={y} active={view === y} onClick={() => setPicked(y)} label={String(y)} count={dated.filter((d) => d.year === y).length} />
        ))}
        </div>
        <select
          aria-label="Open another year"
          className={`${select} shrink-0 text-muted-foreground`}
          value=""
          onChange={(e) => {
            const y = Number(e.target.value);
            if (!y) return;
            setExtra((x) => [...x, y]);
            setPicked(y);
          }}
        >
          <option value="">Other year…</option>
          {older.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      <div className="rounded-lg border border-border">
        <div className="flex flex-col gap-3 border-b border-border bg-secondary/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-semibold">{view === "permanent" ? "Permanent documents" : `${view}`}</p>
            <p className="text-xs text-muted-foreground">
              {view === "permanent" ? "Kept from year to year: IDs, formation papers, records of what an asset cost. " : ""}
              {`${inView.length} file${inView.length === 1 ? "" : "s"}${inView.length ? ` · ${formatBytes(bytes)}` : ""}`}
              {view !== "permanent" && view > thisYear && (uploadOpen ? ` · open early for ${view}` : ` · opens for uploads on December 1, ${view - 1}`)}
            </p>
          </div>
          {zip && (
            <a href={zip} className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-muted sm:self-auto">
              <Download className="h-4 w-4" /> Download all
            </a>
          )}
        </div>

        {onUpload && uploadOpen && (
          <div className="flex flex-col gap-2 border-b border-border px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center">
            <label className="text-sm text-muted-foreground sm:mr-1" htmlFor="upload-folder">
              Upload to
            </label>
            <select id="upload-folder" className={`${select} sm:w-64`} value={target} onChange={(e) => setFolder(e.target.value)}>
              {choices.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
            {uploadExtras}
            <UploadButton multiple disabled={uploading || !!photos} label={uploading ? "Uploading…" : view === "permanent" ? "Upload" : `Upload to ${view}`} onFiles={pick} />
            {photos && (
              <div className="w-full space-y-2 rounded-md border border-border bg-secondary/40 p-3 sm:basis-full">
                <p className="text-sm">{photos.length} photos selected</p>
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-0.5" checked={asOne} onChange={(e) => setAsOne(e.target.checked)} /> These photos are pages of one document
                </label>
                {asOne && (
                  <div className="space-y-1">
                    <label htmlFor="combine-name" className="text-xs text-muted-foreground">
                      Document name
                    </label>
                    <Input id="combine-name" value={docName} maxLength={120} placeholder="e.g. 2026 W-2 Acme" onChange={(e) => setDocName(e.target.value)} />
                    <p className="text-xs text-muted-foreground">The pages are combined into one PDF, in the order you picked them.</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" disabled={asOne && !docName.trim()} onClick={sendPhotos}>
                    {asOne ? "Upload as one document" : `Upload ${photos.length} files`}
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setPhotos(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            {FOLDER_HELP[target] && (
              <p className="text-xs text-muted-foreground sm:basis-full">
                {FOLDER_HELP[target]}
                {taxHint && `. Tax forms go in the year the return is filed: a ${view - 1} W-2 belongs in ${view}.`}
              </p>
            )}
          </div>
        )}

        <div className="space-y-4 p-4">
          {inView.length > 10 && <ListSearch value={search} onChange={setSearch} placeholder={view === "permanent" ? "Search permanent documents" : `Search ${view} documents`} />}
          {inView.length > 0 && !found.length && <p className="text-sm text-muted-foreground">No documents match the search.</p>}
          {groups.map((g) => {
            const folders = g.folders.filter((f) => found.some((d) => d.folder === f));
            if (folders.length === 0) return null;
            return (
              <div key={g.key}>
                {view !== "permanent" && <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{g.label}</p>}
                <div className="space-y-3">
                  {folders.map((f) => (
                    <div key={f}>
                      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground" title={FOLDER_HELP[f]}>
                        <FolderOpen className="h-3.5 w-3.5" aria-hidden /> {f}
                      </p>
                      <FolderFiles key={`${view}-${f}`} files={found.filter((d) => d.folder === f)} resetKey={search} meta={meta} actions={actions} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {inView.length === 0 && <p className="text-sm text-muted-foreground">{view === "permanent" ? "Nothing filed here yet." : `Nothing filed in ${view} yet.`}</p>}
          {empty.length > 0 && (
            <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground">
              <FolderClosed className="h-3.5 w-3.5" aria-hidden />
              <span>{inView.length ? "Empty folders:" : "Folders ready:"}</span>
              {empty.map((f, i) => (
                <span key={f} title={FOLDER_HELP[f]}>
                  {f}
                  {i < empty.length - 1 ? " ·" : ""}
                </span>
              ))}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** One folder's files, 10 at a time when it holds many. */
function FolderFiles({ files, resetKey, meta, actions }: { files: Doc[]; resetKey: string; meta: (d: Doc) => ReactNode; actions: (d: Doc) => ReactNode }) {
  const paged = usePaged(files, { resetKey });
  return (
    <>
      <ul className="divide-y divide-border rounded-lg border border-border">
        {paged.items.map((d) => (
          <li key={d.id} className="flex items-center gap-3 px-3 py-2.5 sm:px-4">
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm">{d.filename}</p>
              <p className="truncate text-xs text-muted-foreground">{meta(d)}</p>
            </div>
            <div className="flex shrink-0 items-center gap-0.5">{actions(d)}</div>
          </li>
        ))}
      </ul>
      <Pager paged={paged} noun="files" compact className="mt-2 border-t-0 pt-0" />
    </>
  );
}

function YearTab({ active, onClick, label, count, icon }: { active: boolean; onClick: () => void; label: string; count: number; icon?: ReactNode }) {
  return (
    <Button
      type="button"
      role="tab"
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      size="sm"
      variant={active ? "default" : "outline"}
      className="shrink-0 gap-1.5 rounded-full"
      onClick={onClick}
    >
      {icon}
      {label}
      {count > 0 && <span className={`tabular rounded-full px-1.5 text-[11px] ${active ? "bg-black/25" : "bg-muted text-muted-foreground"}`}><span aria-hidden>{count}</span><span className="sr-only">{`, ${count} ${count === 1 ? "document" : "documents"}`}</span></span>}
    </Button>
  );
}

/** The line under a file's name: entity (when the person sees several), size, upload date. */
export function docMeta(d: Doc, entity?: string): string {
  return [entity, formatBytes(d.sizeBytes), `uploaded ${formatDate(d.createdAt)}`].filter(Boolean).join(" · ");
}
